import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Heading from "@tiptap/extension-heading";
import FontFamily from "@tiptap/extension-font-family";
import { TextStyle } from "@tiptap/extension-text-style";
import { useEffect } from "react";
import { FIELD_ERROR, FIELD_LABEL } from "./formFieldStyles";

const TextEditor = ({ label, value, onChange, error }) => {
  const editor = useEditor({
    extensions: [
      // Disable default heading to avoid conflict
      StarterKit.configure({
        heading: false,
        bulletList: {
          keepMarks: true,
        },
        orderedList: {
          keepMarks: true,
        },
      }),

      // Custom heading (now works properly)
      Heading.configure({ levels: [1, 2, 3, 4] }),

      Underline,
      TextStyle,
      FontFamily,

      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],

    content: value || "",

    editorProps: {
      attributes: {
        // prose ships large paragraph margins, so Enter left a gap several
        // lines tall. Zeroing them makes a new line behave like the Note
        // textarea. The body size is pinned to the 13px every other field
        // uses, since prose sets its own.
        class:
          "prose prose-sm max-w-none text-[13px] text-gray-900 focus:outline-none min-h-[120px] cursor-text " +
          "[&_p]:my-0 [&_p]:text-[13px] [&_p]:leading-normal [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0 [&_li]:text-[13px] " +
          "[&_h1]:my-2 [&_h2]:my-2 [&_h3]:my-2 [&_h4]:my-2",
      },
    },

    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Prevent cursor jump issue
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const IconBtn = ({ onClick, active, icon }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border text-[13px] transition
        ${active
          ? "border-brand-600 bg-brand-600 text-white"
          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-100"}
      `}
    >
      {icon}
    </button>
  );

  return (
    <div>
      {label && <label className={FIELD_LABEL}>{label}</label>}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-t-lg border border-gray-200 bg-gray-50 p-2.5">
        
        {/* Font */}
        <select
          className="border border-gray-200 rounded-lg px-2 py-1 text-[13px]"
          onChange={(e) =>
            editor.chain().focus().setFontFamily(e.target.value).run()
          }
        >
          <option value="">Font</option>
          <option value="Inter">Inter</option>
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times</option>
        </select>

        {/* Heading */}
        <select
          className="border border-gray-200 rounded-lg px-2 py-1 text-[13px]"
          onChange={(e) => {
            const level = Number(e.target.value);

            if (!level) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().setHeading({ level }).run(); // ✅ FIX
            }
          }}
        >
          <option value="0">Normal</option>
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
        </select>

        {/* Formatting */}
        <IconBtn
          icon="𝐁"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <IconBtn
          icon="𝑰"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <IconBtn
          icon="U̲"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <IconBtn
          icon="S̶"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />

        {/* Lists */}
        <IconBtn
          icon="•"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <IconBtn
          icon="1."
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />

        {/* Alignment */}
        <IconBtn
          icon="⬅"
          onClick={() =>
            editor.chain().focus().setTextAlign("left").run()
          }
        />
        <IconBtn
          icon="⬍"
          onClick={() =>
            editor.chain().focus().setTextAlign("center").run()
          }
        />
        <IconBtn
          icon="➡"
          onClick={() =>
            editor.chain().focus().setTextAlign("right").run()
          }
        />
      </div>

      {/* Editor */}
      <div
        className="cursor-text rounded-b-lg border border-t-0 border-gray-200 bg-white px-3 py-2.5 transition
          hover:border-gray-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100"
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>

      {error && <p className={FIELD_ERROR}>{error}</p>}
    </div>
  );
};

export default TextEditor;